import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Star, CheckCircle2, Award, MessageSquareText, Wrench, Package } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";

const CATEGORIES_UNUSED = [
  { key: "professionalism",    label: "Professionalism",    icon: Award },
  { key: "communication",      label: "Communication",      icon: MessageSquareText },
  { key: "build_quality",      label: "Build Quality",      icon: Wrench },
  { key: "overall_experience", label: "Overall Experience", icon: Package },
];

const scoreLabelEnUnused = (v) => {
  if (v == null) return "";
  if (v >= 4.6) return "EXCELLENT";
  if (v >= 4.0) return "GREAT";
  if (v >= 3.0) return "GOOD";
  if (v >= 2.0) return "COULD BE BETTER";
  return "NEEDS IMPROVEMENT";
};

function initialsOf(name) {
  if (!name) return "•";
  const parts = String(name).trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "•";
}

function InlineAvatar({ name, photoUrl, size = 40 }) {
  const [failed, setFailed] = useState(false);
  const show = !!photoUrl && !failed;
  return (
    <span
      className="rounded-full overflow-hidden flex items-center justify-center shrink-0"
      style={{
        width: size, height: size,
        border: "1px solid rgba(0,229,255,0.35)",
        background: "rgba(0,229,255,0.06)",
        boxShadow: "0 0 8px rgba(0,229,255,0.15)",
      }}
    >
      {show ? (
        <img src={photoUrl} alt={name || "Technician"} className="w-full h-full object-cover" onError={() => setFailed(true)} />
      ) : (
        <span className="text-white text-[13px] font-semibold">{initialsOf(name)}</span>
      )}
    </span>
  );
}

function StarRow({ value, onChange, testid }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1.5" data-testid={testid}>
      {[1,2,3,4,5].map((n) => {
        const filled = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            aria-label={`${n} star${n>1?'s':''}`}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange(n)}
            data-testid={`${testid}-star-${n}`}
            className="p-0.5 focus:outline-none transition-transform active:scale-95"
            style={{ color: filled ? "var(--accent)" : "rgba(120,160,220,0.4)" }}
          >
            <Star size={22} strokeWidth={1.7} fill={filled ? "var(--accent)" : "transparent"} style={{ filter: filled ? "drop-shadow(0 0 6px rgba(0,229,255,0.65))" : "none" }} />
          </button>
        );
      })}
    </div>
  );
}

export function RatingSection({ token, technician, status }) {
  const { t } = useLang();
  const CATEGORIES = [
    { key: "professionalism",    label: t("rating_professionalism"), icon: Award },
    { key: "communication",      label: t("rating_communication"),   icon: MessageSquareText },
    { key: "build_quality",      label: t("rating_build_quality"),   icon: Wrench },
    { key: "overall_experience", label: t("rating_overall"),         icon: Package },
  ];
  const scoreLabel = (v) => {
    if (v == null) return "";
    if (v >= 4.6) return t("score_excellent");
    if (v >= 4.0) return t("score_great");
    if (v >= 3.0) return t("score_good");
    if (v >= 2.0) return t("score_could");
    return t("score_needs");
  };
  const [existing, setExisting] = useState(null);
  const [ratings, setRatings] = useState({ professionalism: 0, communication: 0, build_quality: 0, overall_experience: 0 });
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  // Only fetch/render for completed builds
  useEffect(() => {
    if (status !== "COMPLETED" || !token) return;
    let alive = true;
    axios.get(`${API}/live/${token}/rating`, { timeout: 6000 })
      .then((r) => { if (alive) setExisting(r.data || {submitted:false}); })
      .catch(() => { if (alive) setExisting({submitted:false}); });
    return () => { alive = false; };
  }, [status, token]);

  if (status !== "COMPLETED") return null;

  const allSelected = Object.values(ratings).every((v) => v >= 1 && v <= 5);
  const finalRaw = allSelected
    ? (ratings.professionalism + ratings.communication + ratings.build_quality + ratings.overall_experience) / 4
    : null;
  const finalDisplay = finalRaw != null ? finalRaw.toFixed(1) : "—";

  const submit = async () => {
    if (!allSelected || busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/live/${token}/rating`, { ...ratings, comment: comment.trim() });
      setExisting({ submitted: true, final_rating: data.final_rating });
      toast.success(t("rating_submitted_toast"));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        try { const r = await axios.get(`${API}/live/${token}/rating`); setExisting(r.data); } catch {}
        toast.info(t("rating_already_toast"));
      } else {
        toast.error(t("rating_error_toast"));
      }
    } finally { setBusy(false); }
  };

  // Still loading previous state
  if (existing == null) return null;

  const techName  = technician?.name || "";
  const techPhoto = technician?.profile_photo_url || "";

  // Already-submitted state (idempotent — refresh brings customer back here)
  if (existing.submitted) {
    return (
      <div
        className="rounded-xl border p-5 md:p-6"
        style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0d0d10" }}
        data-testid="rating-thank-you"
      >
        <div className="flex items-center gap-3">
          <InlineAvatar name={techName} photoUrl={techPhoto} />
          <div>
            <div className="text-white text-base font-semibold">{t("rating_thanks_title")}</div>
            <div className="text-white/55 text-sm">{t("rating_thanks_desc_prefix")}{techName || t("rating_default_name")}.</div>
          </div>
        </div>
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-md" style={{ background: "rgba(0,229,255,0.06)", border: "1px solid rgba(0,229,255,0.28)" }}>
          <Star size={14} fill="var(--accent)" style={{ color: "var(--accent)" }} />
          <span className="text-[13px] font-semibold" style={{ color: "var(--accent)" }} data-testid="submitted-final-rating">
            {Number(existing.final_rating ?? 0).toFixed(1)} / 5
          </span>
        </div>
      </div>
    );
  }

  // Editable form
  return (
    <div
      className="rounded-xl border p-5 md:p-6"
      style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0d0d10" }}
      data-testid="rating-section"
    >
      <div className="flex items-center gap-3 mb-4">
        <InlineAvatar name={techName} photoUrl={techPhoto} />
        <div>
          <div className="text-white text-base font-semibold" data-testid="rating-tech-name">{techName || t("rating_default_name")}</div>
          <div className="text-white/55 text-[13px]">{t("rating_prompt")}</div>
        </div>
      </div>

      <div className="space-y-3">
        {CATEGORIES.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.key} className="flex items-center justify-between gap-3 py-1">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: "rgba(0,229,255,0.06)", color: "var(--accent)", border: "1px solid rgba(0,229,255,0.2)" }}>
                  <Icon size={14} />
                </span>
                <span className="text-white text-[13px] md:text-sm font-medium">{c.label}</span>
              </div>
              <StarRow value={ratings[c.key]} onChange={(v) => setRatings((r) => ({ ...r, [c.key]: v }))} testid={`rating-${c.key}`} />
            </div>
          );
        })}
      </div>

      <div className="mt-4">
        <div className="text-white/55 text-[12px] mb-1.5">{t("rating_comment_prompt")} <span className="text-white/30">{t("rating_optional")}</span></div>
        <div className="relative">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 500))}
            maxLength={500}
            placeholder={t("rating_comment_placeholder")}
            className="w-full min-h-[76px] resize-none bg-black/40 border rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[rgba(0,229,255,0.45)]"
            style={{ borderColor: "rgba(255,255,255,0.08)" }}
            data-testid="rating-comment"
          />
          <div className="absolute right-3 bottom-2 text-[11px] text-white/40 tabular-nums" data-testid="rating-comment-count">{comment.length}/500</div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="text-[12px] tracking-[0.22em] uppercase" style={{ color: finalRaw != null ? "var(--accent)" : "rgba(255,255,255,0.4)" }}>
          <span data-testid="rating-live-preview">{finalDisplay} / 5</span>
          <span className="ml-2 text-white/55">{finalRaw != null ? scoreLabel(finalRaw) : t("rating_rate_all")}</span>
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={!allSelected || busy}
          className="h-11 px-6 rounded-lg text-white text-sm font-semibold tracking-wide transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            background: allSelected && !busy ? "linear-gradient(180deg, #0092ff 0%, #0062c9 100%)" : "rgba(255,255,255,0.05)",
            border: "1px solid rgba(0,229,255,0.5)",
            boxShadow: allSelected && !busy ? "0 8px 22px -6px rgba(0,146,255,0.55), inset 0 1px 0 rgba(255,255,255,0.15)" : "none",
          }}
          data-testid="submit-rating-button"
        >
          {busy ? t("rating_submitting") : t("rating_submit")}
        </button>
      </div>
    </div>
  );
}
